import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "./ui/alert-dialog";
import { Trash2, ShieldCheck, ShieldOff } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleDateString("pt-BR");

export const ConfirmDialog = ({ open, onOpenChange, title, description, onConfirm, disabled, children, testid }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent data-testid={testid}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      {children}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancelar</AlertDialogCancel>
        <AlertDialogAction disabled={disabled} onClick={onConfirm} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`${testid}-confirm`}>
          Excluir
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const Row = ({ u, onKeep, onDelete }) => (
  <tr className={`border-b border-border/50 ${u.two_months ? "bg-rose-500/10" : ""}`} data-testid={`expired-row-${u.id}`}>
    <td className="py-2">
      <div className="font-medium">{u.name}</div>
      <div className="text-xs text-muted-foreground">{u.email}</div>
    </td>
    <td><Badge variant="outline">{u.plan_label}</Badge></td>
    <td>
      <span className="text-rose-500">● Inativo</span>
      {u.two_months && <div className="text-[10px] font-bold text-rose-500" data-testid={`expired-two-months-${u.id}`}>2 meses inativo — aguardando sua confirmação para excluir</div>}
    </td>
    <td className="font-mono text-muted-foreground">{fmt(u.expired_at)}</td>
    <td>
      {u.renewal_pending && <Badge variant="outline" className="mr-1 text-amber-500 border-amber-500/40">Reativação solicitada</Badge>}
      {u.keep_inactive && <Badge variant="outline" className="text-sky-500 border-sky-500/40" data-testid={`expired-kept-${u.id}`}>Mantido inativo</Badge>}
    </td>
    <td className="text-right whitespace-nowrap">
      <Button size="sm" variant="outline" onClick={() => onKeep(u)} data-testid={`expired-keep-btn-${u.id}`}>
        {u.keep_inactive ? <ShieldOff className="h-3 w-3 mr-1" /> : <ShieldCheck className="h-3 w-3 mr-1" />}
        {u.keep_inactive ? "Desproteger" : "Manter inativo"}
      </Button>
      <Button size="sm" variant="outline" className="ml-2 text-rose-500" onClick={() => onDelete(u)} data-testid={`expired-delete-btn-${u.id}`}>
        <Trash2 className="h-3 w-3 mr-1" /> Excluir
      </Button>
    </td>
  </tr>
);

export const ExpiredAccounts = () => {
  const [list, setList] = useState(null);
  const [target, setTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");

  const load = useCallback(() => {
    api.get("/admin/expired").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const keep = async (u) => {
    try {
      await api.post(`/admin/expired/${u.id}/keep`, { keep: !u.keep_inactive });
      toast.success(u.keep_inactive ? "Conta liberada para exclusão em massa" : "Conta mantida como inativa");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeOne = async () => {
    try {
      await api.delete(`/admin/expired/${target.id}`);
      toast.success("Conta excluída");
      setTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeAll = async () => {
    try {
      const { data } = await api.post("/admin/expired/delete-all", { confirm: bulkText });
      toast.success(`${data.deleted} conta(s) excluída(s)`);
      setBulkOpen(false);
      setBulkText("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deletable = (list || []).filter((u) => !u.keep_inactive).length;

  return (
    <Card className="p-6 min-w-0 max-w-full overflow-hidden" data-testid="expired-accounts">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display text-lg font-bold">Contas vencidas</div>
          <p className="text-xs text-muted-foreground">Nunca são excluídas automaticamente. Contas "Mantido inativo" ficam fora da exclusão em massa.</p>
        </div>
        <Button variant="outline" className="text-rose-500" disabled={!deletable} onClick={() => setBulkOpen(true)} data-testid="expired-delete-all-btn">
          <Trash2 className="h-4 w-4 mr-2" /> Excluir todas as vencidas ({deletable})
        </Button>
      </div>
      {!list ? (
        <p className="text-sm text-muted-foreground">Carregando...</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="expired-empty">Nenhuma conta vencida no momento.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <th className="text-left py-2">Usuário</th>
                <th className="text-left">Plano</th>
                <th className="text-left">Status</th>
                <th className="text-left">Vencimento</th>
                <th className="text-left">Situação</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="expired-table">
              {[...list].sort((a, b) => Number(b.two_months) - Number(a.two_months)).map((u) => <Row key={u.id} u={u} onKeep={keep} onDelete={setTarget} />)}
            </tbody>
          </table>
        </div>
      )}
      <ConfirmDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)} testid="expired-delete-dialog"
        title="Excluir conta vencida?"
        description={target ? `${target.name} será removido definitivamente. Os indicados dele sobem para o patrocinador acima na matriz.` : ""}
        onConfirm={removeOne} />
      <ConfirmDialog open={bulkOpen} onOpenChange={setBulkOpen} testid="expired-bulk-dialog"
        title={`Excluir ${deletable} conta(s) vencida(s)?`}
        description='Esta ação é definitiva. Contas "Mantido inativo" não serão afetadas. Digite EXCLUIR para confirmar.'
        onConfirm={removeAll} disabled={bulkText !== "EXCLUIR"}>
        <Input value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder="EXCLUIR" data-testid="expired-bulk-input" />
      </ConfirmDialog>
    </Card>
  );
};
